import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Edit, Clock, Trash2, MapPin, Mail, Phone, User, Search } from 'lucide-react';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { functions, db } from '../../../firebase';

const PAGE_SIZE = 50;
const ALL_COUNTRIES = 'ALL';

const MosqueManagement = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const [metaCounts, setMetaCounts] = useState({});        // { [cc]: count } from mosqueIndex/_meta ({} if missing/unreadable)
  const [countryCode, setCountryCode] = useState('');      // '' until loaded; ALL_COUNTRIES when there is no index
  const [selfManaged, setSelfManaged] = useState([]);      // full docs from getAllMosques (all statuses, with `admin`)
  const [catalogEntries, setCatalogEntries] = useState([]); // [{ id, n, c, lat, lng, src: 'c' }] from mosqueIndex/{cc}
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [catalogDocs, setCatalogDocs] = useState({});      // id -> full catalogMosques doc for the visible slice
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');                // non-blocking: index missing or unreadable
  // catalogMosques ids already requested (in flight, loaded or not found). A ref, not state, so the
  // loader effect does not depend on what it loads and cannot loop on a missing document.
  const requestedCatalogIds = useRef(new Set());

  // Self-managed mosques come from the callable: every status (pending ones must stay editable),
  // `admin` joined server-side, 86 docs. Catalogue mosques come from the index (thousands).
  // `_meta` is read separately: if it is missing or unreadable, the self-managed list must still show.
  const loadMosques = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setNotice('');
      const [metaResult, ownResult] = await Promise.allSettled([
        getDoc(doc(db, 'mosqueIndex', '_meta')),
        httpsCallable(functions, 'getAllMosques')({ limit: 500 })
      ]);

      let counts = {};
      if (metaResult.status === 'fulfilled') {
        const data = metaResult.value.exists() ? metaResult.value.data().countries || {} : {};
        Object.entries(data).forEach(([code, v]) => { counts[code] = v?.count ?? 0; });
        if (Object.keys(counts).length === 0) {
          setNotice('Mosque index not built yet (run rebuildMosqueIndex); showing all self-managed mosques.');
        }
      } else {
        console.error('Error loading mosque index meta:', metaResult.reason);
        counts = {};
        setNotice('Mosque index unavailable; showing all self-managed mosques.');
      }
      setMetaCounts(counts);

      const hasIndex = Object.keys(counts).length > 0;
      setCountryCode((current) => {
        if (!hasIndex) return ALL_COUNTRIES;
        if (current && current !== ALL_COUNTRIES) return current;
        return counts.NL !== undefined ? 'NL' : Object.keys(counts).sort()[0];
      });

      if (ownResult.status === 'fulfilled') {
        setSelfManaged(Array.isArray(ownResult.value.data?.mosques) ? ownResult.value.data.mosques : []);
      } else {
        console.error('Error loading mosques:', ownResult.reason);
        setError(ownResult.reason?.message || 'Failed to load mosques');
      }
    } catch (err) {
      console.error('Error loading mosques:', err);
      setError(err.message || 'Failed to load mosques');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMosques();
  }, [loadMosques]);

  const hasIndex = Object.keys(metaCounts).length > 0;

  // Country options: the union of the index countries and the countries of the self-managed mosques,
  // so a pending mosque, or one in a country the index does not list, is always reachable.
  const countries = useMemo(() => {
    const local = {};
    selfManaged.forEach((m) => {
      const cc = (m.location?.countryCode || '').toUpperCase();
      if (cc) local[cc] = (local[cc] || 0) + 1;
    });
    const codes = new Set([...Object.keys(metaCounts), ...Object.keys(local)]);
    const list = [...codes]
      .sort((a, b) => a.localeCompare(b))
      .map((code) => ({ code, count: metaCounts[code] !== undefined ? metaCounts[code] : local[code] }));
    if (!hasIndex) list.unshift({ code: ALL_COUNTRIES, count: selfManaged.length });
    return list;
  }, [metaCounts, selfManaged, hasIndex]);

  useEffect(() => {
    setCatalogEntries([]);
    if (!countryCode || countryCode === ALL_COUNTRIES) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'mosqueIndex', countryCode));
        if (cancelled) return;
        setCatalogEntries((snap.exists() ? snap.data().entries || [] : []).filter((e) => e.src === 'c'));
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading mosque index:', err);
        setError(err.message || 'Failed to load catalogue');
      }
    })();
    return () => { cancelled = true; };
  }, [countryCode]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (name, city) => !q || `${name || ''} ${city || ''}`.toLowerCase().includes(q);
    const own = selfManaged
      .filter((m) => countryCode === ALL_COUNTRIES
        || !m.location?.countryCode
        || m.location.countryCode.toUpperCase() === countryCode)
      .filter((m) => matches(m.displayName || m.name, m.location?.city))
      .map((m) => ({ key: `m:${m.id}`, src: 'm', doc: m }));
    const cat = catalogEntries
      .filter((e) => matches(e.n, e.c))
      .map((e) => ({ key: `c:${e.id}`, src: 'c', entry: e }));
    return [...own, ...cat];
  }, [selfManaged, catalogEntries, search, countryCode]);

  // Reset paging in the same update as the filter change, so the loader below never sees a stale
  // visibleCount for the new result set (at most PAGE_SIZE catalogue reads per page).
  const handleCountryChange = (code) => {
    setVisibleCount(PAGE_SIZE);
    setCountryCode(code);
  };

  const handleSearchChange = (value) => {
    setVisibleCount(PAGE_SIZE);
    setSearch(value);
  };

  // Full catalogMosques docs for the visible slice only: at most PAGE_SIZE reads per page, each id once.
  useEffect(() => {
    const ids = filtered.slice(0, visibleCount)
      .filter((x) => x.src === 'c')
      .map((x) => x.entry.id)
      .filter((id) => !requestedCatalogIds.current.has(id));
    if (ids.length === 0) return;
    ids.forEach((id) => requestedCatalogIds.current.add(id));
    (async () => {
      try {
        const snaps = await Promise.all(ids.map((id) => getDoc(doc(db, 'catalogMosques', id))));
        setCatalogDocs((prev) => {
          const next = { ...prev };
          snaps.forEach((snap) => { if (snap.exists()) next[snap.id] = { id: snap.id, src: 'c', ...snap.data() }; });
          return next;
        });
      } catch (err) {
        ids.forEach((id) => requestedCatalogIds.current.delete(id)); // allow a retry on the next page/filter change
        console.error('Error loading catalogue documents:', err);
        setError(err.message || 'Failed to load mosque details');
      }
    })();
  }, [filtered, visibleCount]);

  // The card JSX below maps over `mosques`; keep that name.
  const mosques = filtered.slice(0, visibleCount)
    .map((x) => (x.src === 'm' ? x.doc : catalogDocs[x.entry.id]))
    .filter(Boolean);

  const handleDelete = async (mosqueId, mosqueName) => {
    if (deleting) return;
    if (!window.confirm(`Are you sure you want to delete "${mosqueName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(mosqueId);
      setError('');
      const deleteMosque = httpsCallable(functions, 'deleteMosque');
      await deleteMosque({ mosqueId });
      await loadMosques();
    } catch (error) {
      console.error('Error deleting mosque:', error);
      setError(`Failed to delete mosque: ${error.message}`);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Mosque Management
          </h1>
          <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Create and manage mosques, prayer times, and assignments
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/mosques/create')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-colors ${
            isDarkMode
              ? 'bg-emerald-500 text-black hover:bg-emerald-400'
              : 'bg-gray-900 text-white hover:bg-gray-800'
          }`}
        >
          <Plus className="w-5 h-5" />
          Add Mosque
        </button>
      </div>

      {error && (
        <div className={`p-4 rounded-xl border text-sm ${
          isDarkMode
            ? 'bg-red-900/20 border-red-500/30 text-red-300'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {error}
        </div>
      )}

      {notice && (
        <div className={`p-4 rounded-xl border text-sm ${
          isDarkMode
            ? 'bg-amber-900/20 border-amber-500/30 text-amber-300'
            : 'bg-amber-50 border-amber-200 text-amber-700'
        }`}>
          {notice}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <select
          value={countryCode}
          onChange={(e) => handleCountryChange(e.target.value)}
          className={`px-4 py-2.5 rounded-xl border ${
            isDarkMode ? 'bg-[#0A0A0A] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
          }`}
        >
          {countries.map((c) => (
            <option key={c.code} value={c.code}>{c.code === ALL_COUNTRIES ? 'All countries' : c.code} ({c.count})</option>
          ))}
        </select>
        <div className="relative flex-1">
          <Search className={`w-4 h-4 absolute left-3 top-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by name or city"
            className={`w-full pl-9 pr-4 py-2.5 rounded-xl border ${
              isDarkMode ? 'bg-[#0A0A0A] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>
        <span className={`self-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {filtered.length} mosques
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className={`p-12 rounded-[24px] border text-center ${
          isDarkMode ? 'bg-[#111111] border-gray-800' : 'bg-white border-gray-100'
        }`}>
          <Building2 className={`w-16 h-16 mx-auto mb-4 ${
            isDarkMode ? 'text-gray-600' : 'text-gray-400'
          }`} />
          <h3 className={`text-lg font-semibold mb-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            No mosques yet
          </h3>
          <p className={`text-sm mb-4 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Add your first mosque to get started
          </p>
          <button
            onClick={() => navigate('/admin/mosques/create')}
            className={`px-6 py-2.5 rounded-xl transition-colors ${
              isDarkMode
                ? 'bg-emerald-500 text-black hover:bg-emerald-400'
                : 'bg-gray-900 text-white hover:bg-gray-800'
            }`}
          >
            Add Mosque
          </button>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {mosques.map(mosque => (
            <div
              key={`${mosque.src === 'c' ? 'c' : 'm'}:${mosque.id}`}
              className={`p-6 rounded-[24px] border ${
                isDarkMode ? 'bg-[#111111] border-gray-800' : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className={`text-lg font-semibold mb-1 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {mosque.name}
                    {mosque.src === 'c' && (
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium align-middle ${isDarkMode ? 'bg-emerald-900 text-emerald-200' : 'bg-emerald-100 text-emerald-800'}`}>
                        Imported
                      </span>
                    )}
                  </h3>
                  {mosque.displayName && mosque.displayName !== mosque.name && (
                    <p className={`text-sm ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      {mosque.displayName}
                    </p>
                  )}
                </div>

                {mosque.adminId ? (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isDarkMode
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    Has Admin
                  </span>
                ) : (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isDarkMode
                      ? 'bg-gray-800 text-gray-400'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    No Admin
                  </span>
                )}
              </div>

              <div className="space-y-2 mb-4">
                {mosque.location && (
                  <div className="flex items-start gap-2">
                    <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                    <p className={`text-sm ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {mosque.location.street && `${mosque.location.street}, `}
                      {mosque.location.city}
                      {mosque.location.country && `, ${mosque.location.country}`}
                    </p>
                  </div>
                )}

                {mosque.email && (
                  <div className="flex items-center gap-2">
                    <Mail className={`w-4 h-4 flex-shrink-0 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                    <p className={`text-sm ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {mosque.email}
                    </p>
                  </div>
                )}

                {mosque.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className={`w-4 h-4 flex-shrink-0 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                    <p className={`text-sm ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {mosque.phone}
                    </p>
                  </div>
                )}

                {mosque.admin && (
                  <div className="flex items-center gap-2">
                    <User className={`w-4 h-4 flex-shrink-0 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                    <p className={`text-sm ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Admin: {mosque.admin.name}
                    </p>
                  </div>
                )}
              </div>

              {mosque.src !== 'c' && (
              <div className={`flex items-center gap-2 pt-4 border-t ${
                isDarkMode ? 'border-gray-800' : 'border-gray-200'
              }`}>
                <button
                  onClick={() => navigate(`/admin/mosques/${mosque.id}/edit`)}
                  disabled={Boolean(deleting)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                    isDarkMode
                      ? 'bg-gray-800 text-white hover:bg-gray-700'
                      : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                  } ${deleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>

                <button
                  onClick={() => navigate(`/admin/mosques/${mosque.id}/prayer-times`)}
                  disabled={Boolean(deleting)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                    isDarkMode
                      ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                      : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                  } ${deleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Clock className="w-4 h-4" />
                  Prayer Times
                </button>

                <button
                  onClick={() => handleDelete(mosque.id, mosque.name)}
                  disabled={deleting === mosque.id}
                  className={`px-4 py-2 rounded-xl transition-colors ${
                    isDarkMode
                      ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:bg-red-500/10'
                      : 'bg-red-100 text-red-700 hover:bg-red-200 disabled:bg-red-50'
                  }`}
                >
                  {deleting === mosque.id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current"></div>
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
              )}

              <p className={`text-xs ${mosque.src === 'c' ? `pt-4 border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}` : 'mt-3'} ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {mosque.src === 'c' ? 'Managed by the yearly import' : 'Self-managed'}
                {mosque.prayerTimesCoverage ? ` · times ${mosque.prayerTimesCoverage.from} – ${mosque.prayerTimesCoverage.to}` : ' · no prayer times uploaded'}
              </p>
            </div>
          ))}
        </div>
        {visibleCount < filtered.length && (
          <div className="flex justify-center">
            <button
              onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
              className={`px-6 py-2.5 rounded-xl transition-colors ${
                isDarkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
              }`}
            >
              Show {Math.min(PAGE_SIZE, filtered.length - visibleCount)} more
            </button>
          </div>
        )}
        </>
      )}
    </div>
  );
};

export default MosqueManagement;
